import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Chip, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridComparatorFn,
  GridRenderCellParams,
  GridRowParams,
  GridSortItem,
  GridSortModel,
} from '@mui/x-data-grid';
import { SortKey, StaffPerformanceRecord } from '../types';
import { formatDecimal, formatDisplayName, formatInt, pctDelta, ptsDelta, shortLocationName, signed } from '../utils/metrics';
import { DeltaText } from './DeltaText';
import { OverdueUpdatesCell } from './OverdueUpdatesCell';
import { Sparkline } from './Sparkline';

export interface TeamTableProps {
  records: StaffPerformanceRecord[];
  sortKey: SortKey;
  onSortKey: (key: SortKey) => void;
  onRowClick: (record: StaffPerformanceRecord) => void;
  /** Admins span several locations. */
  showLocation?: boolean;
  /** Marks the signed-in user's own row. */
  selfStaffId?: string | null;
  caption?: string;
  /** Adds the "Overdue updates" column; off until the success-update data is loaded. */
  showUpdates?: boolean;
  /** Opens the client list for a coach from the "Overdue updates" cell. */
  onOpenUpdates?: (record: StaffPerformanceRecord) => void;
  /** Test-only escape hatch: jsdom has no layout, so virtualised rows never render. */
  disableVirtualization?: boolean;
}

/** Flat row so DataGrid can sort on primitive fields; nulls mean "no row in that view". */
export interface TeamRow {
  id: string;
  displayName: string;
  staffId: number;
  locationLabel: string;
  sessionsLatest: number | null;
  meanLatest: number | null;
  activeMonths: number | null;
  changePct: number | null;
  spark: number[];
  latestFrom: number;
  newLatest: number | null;
  nonReturningLatest: number | null;
  netLatest: number | null;
  ratePct: number | null;
  converted: number | null;
  expired: number | null;
  rateChangePts: number | null;
  overdue: number | null;
  activeClients: number | null;
  awaitingFirst: number | null;
  isYou: boolean;
  record: StaffPerformanceRecord;
}

const SORT_FIELDS: Record<SortKey, { field: keyof TeamRow; sort: 'asc' | 'desc' }> = {
  sessions: { field: 'sessionsLatest', sort: 'desc' },
  change: { field: 'changePct', sort: 'desc' },
  net: { field: 'netLatest', sort: 'desc' },
  conversion: { field: 'ratePct', sort: 'desc' },
  overdue: { field: 'overdue', sort: 'desc' },
  name: { field: 'displayName', sort: 'asc' },
  location: { field: 'locationLabel', sort: 'asc' },
};

export function toTeamRow(record: StaffPerformanceRecord, selfStaffId?: string | null): TeamRow {
  const { sessions, clients, conversion } = record;
  const updates = record.success_updates?.counts ?? null;
  const latestFrom = sessions ? sessions.monthly.findIndex((m) => m.period === 'latest_6m') : 0;
  return {
    id: record.id,
    displayName: formatDisplayName(record),
    staffId: record.staff_id,
    locationLabel: shortLocationName(record.home_location),
    sessionsLatest: sessions ? sessions.latest.total : null,
    meanLatest: sessions ? sessions.latest.mean : null,
    activeMonths: sessions ? sessions.latest.activeMonths : null,
    changePct: sessions ? sessions.meanChangePct : null,
    spark: sessions ? sessions.monthly.map((m) => m.count) : [],
    latestFrom: latestFrom === -1 ? 0 : latestFrom,
    newLatest: clients ? clients.latest.new : null,
    nonReturningLatest: clients ? clients.latest.nonReturning : null,
    netLatest: clients ? clients.latest.net : null,
    ratePct: conversion ? conversion.latest.ratePct : null,
    converted: conversion ? conversion.latest.converted : null,
    expired: conversion ? conversion.latest.expired : null,
    rateChangePts: conversion ? conversion.rateChangePts : null,
    overdue: updates ? updates.overdue : null,
    activeClients: updates ? updates.active : null,
    awaitingFirst: updates ? updates.awaiting_first : null,
    isYou: Boolean(selfStaffId) && record.id === selfStaffId,
    record,
  };
}

const modelFor = (key: SortKey): GridSortModel => [{ field: SORT_FIELDS[key].field, sort: SORT_FIELDS[key].sort }];

const keyForField = (field: string | undefined): SortKey | undefined =>
  (Object.keys(SORT_FIELDS) as SortKey[]).find((key) => SORT_FIELDS[key].field === field);

/**
 * Keeps "n/a" rows at the bottom whichever way the column is sorted: DataGrid
 * negates the comparator for descending, so null stands in for −∞ there and
 * +∞ when ascending.
 */
const nullsLast: GridComparatorFn<number | null> = (v1, v2, cellParams) => {
  if (v1 === null && v2 === null) return 0;
  const direction =
    cellParams.api.state.sorting.sortModel.find((item: GridSortItem) => item.field === cellParams.field)?.sort ?? 'desc';
  const fill = direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  return (v1 ?? fill) - (v2 ?? fill);
};

const NA = (
  <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>
    n/a
  </Typography>
);

const numberCell = (value: number | null, render: (value: number) => React.ReactNode) => (value === null ? NA : render(value));

/** Sortable team grid; every column maps to a flat row field so built-in sorting works. */
export const TeamTable: React.FC<TeamTableProps> = ({
  records,
  sortKey,
  onSortKey,
  onRowClick,
  showLocation = false,
  selfStaffId = null,
  caption,
  showUpdates = false,
  onOpenUpdates,
  disableVirtualization = false,
}) => {
  const rows = useMemo(() => records.map((record) => toTeamRow(record, selfStaffId)), [records, selfStaffId]);

  // The grid owns its sort model so every header click (and direction toggle)
  // works; the parent's sort select resets it, and header clicks on a mapped
  // column report back so the select stays in step.
  const [sortModel, setSortModel] = useState<GridSortModel>(() => modelFor(sortKey));
  useEffect(() => {
    setSortModel((current) => (current[0]?.field === SORT_FIELDS[sortKey].field ? current : modelFor(sortKey)));
  }, [sortKey]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model.length ? model : modelFor(sortKey));
    const key = keyForField(model[0]?.field);
    if (key && key !== sortKey) onSortKey(key);
  };

  const columns: GridColDef<TeamRow>[] = useMemo(() => {
    const numeric = { align: 'right' as const, headerAlign: 'right' as const };
    const defs: Array<GridColDef<TeamRow> | null> = [
      {
        field: 'displayName',
        headerName: 'Staff',
        flex: 1,
        minWidth: 240,
        renderCell: (params: GridRenderCellParams<TeamRow>) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 600, bgcolor: 'grey.200', color: 'grey.700' }}>
              {params.row.displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {params.row.displayName}
                </Typography>
                {params.row.isYou ? <Chip label="You" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} /> : null}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Coach · {params.row.staffId}
              </Typography>
            </Box>
          </Box>
        ),
      },
      showLocation
        ? {
            field: 'locationLabel',
            headerName: 'Location',
            width: 150,
            renderCell: (params: GridRenderCellParams<TeamRow>) => <Chip size="small" label={params.row.locationLabel} />,
          }
        : null,
      {
        field: 'sessionsLatest',
        headerName: 'Sessions',
        description: 'Latest 6 months',
        width: 110,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) => numberCell(params.row.sessionsLatest, (value) => formatInt(value)),
      },
      {
        field: 'meanLatest',
        headerName: 'Per month',
        description: 'Mean per month the coach was active · (n mo) = months with a session in view',
        width: 125,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) =>
          numberCell(params.row.meanLatest, (value) => (
            <span>
              {formatDecimal(value)}
              {params.row.activeMonths !== null && params.row.activeMonths < 6 ? (
                <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                  ({params.row.activeMonths} mo)
                </Typography>
              ) : null}
            </span>
          )),
      },
      {
        field: 'changePct',
        headerName: 'vs prior',
        width: 110,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) => <DeltaText delta={pctDelta(params.row.changePct)} />,
      },
      {
        field: 'spark',
        headerName: '12-month trend',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<TeamRow>) =>
          params.row.spark.length ? (
            <Sparkline counts={params.row.spark} latestFrom={params.row.latestFrom} aria-label={`${params.row.displayName} trend`} />
          ) : (
            NA
          ),
      },
      {
        field: 'newLatest',
        headerName: 'New',
        width: 80,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) => numberCell(params.row.newLatest, (value) => formatInt(value)),
      },
      {
        field: 'nonReturningLatest',
        headerName: 'Non-ret.',
        description: 'Non-returning clients',
        width: 95,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) => numberCell(params.row.nonReturningLatest, (value) => formatInt(value)),
      },
      {
        field: 'netLatest',
        headerName: 'Net',
        width: 80,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) =>
          numberCell(params.row.netLatest, (value) => (
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600, color: value > 0 ? '#047857' : value < 0 ? '#B91C1C' : 'text.secondary' }}
            >
              {signed(value)}
            </Typography>
          )),
      },
      {
        field: 'ratePct',
        headerName: 'Conversion',
        width: 135,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) =>
          params.row.expired === null ? (
            NA
          ) : (
            <span>
              <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                {params.row.ratePct === null ? 'n/a' : `${formatDecimal(params.row.ratePct)}%`}
              </Typography>{' '}
              <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
                {params.row.converted} of {params.row.expired}
              </Typography>
            </span>
          ),
      },
      {
        field: 'rateChangePts',
        headerName: 'vs prior',
        width: 110,
        ...numeric,
        sortComparator: nullsLast,
        renderCell: (params: GridRenderCellParams<TeamRow>) => <DeltaText delta={ptsDelta(params.row.rateChangePts)} />,
      },
      showUpdates
        ? {
            field: 'overdue',
            headerName: 'Overdue updates',
            description: 'Active clients whose last success update is more than 6 weeks old',
            width: 160,
            ...numeric,
            sortComparator: nullsLast,
            renderCell: (params: GridRenderCellParams<TeamRow>) => (
              <OverdueUpdatesCell
                overdue={params.row.overdue}
                active={params.row.activeClients}
                awaiting={params.row.awaitingFirst}
                name={params.row.displayName}
                onOpen={() => onOpenUpdates?.(params.row.record)}
              />
            ),
          }
        : null,
      {
        field: 'open',
        headerName: '',
        width: 48,
        sortable: false,
        disableColumnMenu: true,
        renderCell: () => <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />,
      },
    ];
    return defs.filter((def): def is GridColDef<TeamRow> => def !== null);
  }, [showLocation, showUpdates, onOpenUpdates]);

  return (
    <Box>
      {caption ? (
        <Typography variant="subtitle2" sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {caption}
        </Typography>
      ) : null}
      <DataGrid
        rows={rows}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sortingOrder={['desc', 'asc']}
        onRowClick={(params: GridRowParams<TeamRow>) => onRowClick(params.row.record)}
        disableRowSelectionOnClick
        disableVirtualization={disableVirtualization}
        autoHeight
        rowHeight={60}
        hideFooter={rows.length <= 100}
        pageSizeOptions={[100]}
        sx={{
          border: 'none',
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell': { fontVariantNumeric: 'tabular-nums' },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50' },
        }}
      />
    </Box>
  );
};
